import { useMemo, type FC, useState } from "react"
import {
  Stack,
  Text,
  Flex,
  Button,
  StackDivider,
  Box,
  useDisclosure,
  HStack,
} from "@chakra-ui/react"
import { AccountId as CaipAccountId } from "caip"

import ProposalTransferReceipt from "proposal/components/ProposalTransferReceipt"
import SwapReceipt from "proposal/components/receipts/SwapReceipt"
import type {
  AccountId,
  GovernorContract,
  Maybe,
  ExecutableCall,
  Proposal,
  ExecutableCallSwap,
} from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import ProposalCustomReceipt from "proposal/components/ProposalCustomReceipt"
import { useTokenBalances } from "common/hooks/useTokenBalances"
import { getCustomProposalSourceTreasuryAddress } from "governance/helpers/treasury"
import { getChainIdParams } from "web3/helpers/chainId"
import {
  formatFeesForDisplay,
  getTotalFeesFromExecutableCalls,
} from "createProposal/helpers/fee"
import ProposalExecutableCode from "proposal/components/ProposalExecutableCode"
import ProposalCommand from "proposal/components/ProposalCommand"
import { useDevice } from "common/hooks/useDevice"
import TallyFeeInformationModal from "createProposal/components/TallyFeeInformationModal"
import { parseAddressFromTarget } from "web3/helpers/transformers"

type Props = {
  proposal: Proposal
  chainId: string
  governance: {
    slug: string
    contracts: {
      governor: Pick<GovernorContract, "type" | "address">
    }
    timelockId?: Maybe<AccountId>
  }
  governanceId: AccountId
  onNavigateCustomActions?: () => void
}

const ProposalReceipts: FC<Props> = ({
  proposal,
  chainId,
  governanceId,
  governance,
}) => {
  const [showExecutable, setShowExecutable] = useState<boolean>(false)
  const { onLargeDevice } = useDevice()

  const { executableCalls } = proposal
  const tallyFeeInformationModal = useDisclosure()

  const treasuryAddress = useMemo(() => {
    if (!governance) return ""

    const { timelockId } = governance
    const { governor } = governance.contracts

    const treasuryAddress = timelockId
      ? CaipAccountId.parse(timelockId).address
      : governor.address

    return treasuryAddress
  }, [governance])

  const { reference: governanceChainReference } = getChainIdParams(chainId)

  const { tokenBalances } = useTokenBalances(
    governanceChainReference,
    governance.slug === "arbitrum"
      ? (getCustomProposalSourceTreasuryAddress({ slug: "arbitrum" }) as string)
      : treasuryAddress,
  )

  const showExecutableOnly = executableCalls?.every(
    (executableCall) =>
      executableCall.type === null && executableCall.decodedCalldata === null,
  )

  if (
    !executableCalls ||
    executableCalls.length === 0 ||
    executableCalls[0].type === ExecutableCallType.Empty
  )
    return null

  const { transferActions, customActions, swapActions } =
    executableCalls.reduce(
      (prev, executableCall) => {
        const { transferActions, customActions, orcaActions, swapActions } =
          prev

        if (executableCall?.type === ExecutableCallType.Swap) {
          return {
            ...prev,
            swapActions: [...swapActions, executableCall],
          }
        }

        if (executableCall?.type === ExecutableCallType.Orcamanagepod) {
          return {
            ...prev,
            orcaActions: [...orcaActions, executableCall],
          }
        }

        if (
          executableCall.type === ExecutableCallType.Erc20transfer ||
          executableCall.type === ExecutableCallType.Nativetransfer
        ) {
          return {
            ...prev,
            transferActions: [...transferActions, executableCall],
          }
        }

        if (executableCall.type === ExecutableCallType.Erc20transferarbitrum) {
          return {
            ...prev,
            transferActions: [...transferActions, executableCall],
          }
        }

        if (
          executableCall?.type === ExecutableCallType.Custom ||
          executableCall?.decodedCalldata
        ) {
          return {
            ...prev,
            customActions: [...customActions, executableCall],
          }
        }

        return prev
      },
      {
        transferActions: [] as ExecutableCall[],
        customActions: [] as ExecutableCall[],
        orcaActions: [] as ExecutableCall[],
        swapActions: [] as ExecutableCall[],
      },
    )

  const totalFees = getTotalFeesFromExecutableCalls(
    [...transferActions, ...swapActions],
    tokenBalances,
  )

  const feeLabel = formatFeesForDisplay(totalFees)

  if (showExecutableOnly) return null

  return (
    <Stack
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      maxW="700px"
      minW="full"
      spacing={0}
    >
      <Flex
        align="center"
        borderBottomColor="gray.200"
        borderBottomWidth="1px"
        justifyContent="space-between"
        p={5}
        w="full"
      >
        <Text textStyle="body.bold.xl">Actions</Text>
        {!showExecutableOnly && (
          <Button
            variant="secondary"
            onClick={() => setShowExecutable(!showExecutable)}
          >
            {showExecutable ? "Summary" : "Raw"}
          </Button>
        )}
      </Flex>
      <Box p={showExecutable || showExecutableOnly ? 5 : 0}>
        {showExecutable || showExecutableOnly ? (
          <ProposalExecutableCode
            chainId={chainId}
            executableCalls={executableCalls as ExecutableCall[]}
          />
        ) : showExecutable && executableCalls ? (
          <Stack align="flex-end">
            <Stack className="scrollbar" spacing={4} w="full">
              {executableCalls.map(
                ({ calldata, signature, target, value }, i) => {
                  const { address } = parseAddressFromTarget(target)

                  return (
                    <ProposalCommand
                      key={`target_${target}_executable_call`}
                      isDraftProposal
                      calldata={calldata || ""}
                      chainId={chainId}
                      index={i}
                      signature={signature}
                      target={address}
                      value={value}
                    />
                  )
                },
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack divider={<StackDivider borderColor="gray.200" />} spacing={0}>
            <Flex align="center" display={onLargeDevice} p={5}>
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                width="25%"
              >
                Type
              </Text>
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                width="30%"
              >
                Address
              </Text>
              <Text
                color="#475467"
                fontSize="14px"
                fontWeight={400}
                lineHeight="20px"
                width="45%"
              >
                Details
              </Text>
            </Flex>
            {transferActions.map((executableCall, index) => {
              return (
                <ProposalTransferReceipt
                  key={index}
                  chainId={chainId}
                  executableCall={executableCall}
                  governance={governance}
                  governanceId={governanceId}
                />
              )
            })}
            {swapActions.map((swapAction, index) => {
              if (!swapAction.offchaindata) return null

              return (
                <SwapReceipt
                  key={index}
                  meta={swapAction.offchaindata as ExecutableCallSwap}
                  target={swapAction.target}
                />
              )
            })}
            {customActions.map((customAction, index) => {
              if (
                (!customAction.signature &&
                  !customAction.decodedCalldata?.signature) ||
                !customAction.target
              )
                return null

              return (
                <ProposalCustomReceipt
                  key={index}
                  chainId={chainId}
                  customAction={customAction}
                  governorId={governanceId}
                />
              )
            })}
            {feeLabel && (
              <HStack
                align="center"
                fontSize="14px"
                justifyContent="flex-end"
                px={5}
                py={3}
                w="full"
              >
                <Text>{feeLabel}</Text>
                <Text
                  cursor="pointer"
                  textDecoration="underline"
                  textStyle="body.regular.md"
                  onClick={tallyFeeInformationModal.onOpen}
                >
                  What is the fee for?
                </Text>
              </HStack>
            )}
          </Stack>
        )}
      </Box>
      <TallyFeeInformationModal
        isOpen={tallyFeeInformationModal.isOpen}
        onClose={tallyFeeInformationModal.onClose}
      />
    </Stack>
  )
}

export default ProposalReceipts
